import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { pbEngineer, protectedFileUrl } from '../lib/pb';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function PartsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, ready } = useData();

  const part = (data.parts || []).find((p) => p.id === id);

  const openDatasheet = async () => {
    if (!part?.datasheet) return;
    const url = await protectedFileUrl({ id: part.id, collectionName: 'parts_db' }, part.datasheet);
    if (url) window.open(url, '_blank', 'noopener');
  };

  if (!ready) return <div className="page-container"><Skeleton rows={6} /></div>;

  if (!part) {
    return (
      <div className="page-container">
        <EmptyState
          title="Part not found"
          message="This part isn't in the catalogue."
          action="Back to Parts"
          onAction={() => navigate('..', { relative: 'path' })}
        />
      </div>
    );
  }

  const specs = Object.entries(part.specifications || {});

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>{part.name}</h1>
          <p>{part.part_number}{part.manufacturer ? ` · ${part.manufacturer}` : ''}</p>
        </div>
        <button className="secondary-button" onClick={() => navigate('..', { relative: 'path' })}>
          <SafeIcon icon={Icons.ArrowLeft} size={15} />
          Back to Parts
        </button>
      </div>

      <div className="parts-detail">
        <div className="detail-panel">
          <div className="section-header">
            <h3>Details</h3>
            {part.sell_price > 0 && <span className="stock-badge in-stock">£{part.sell_price.toFixed(2)}</span>}
          </div>
          <table className="spec-table">
            <tbody>
              <tr><td>Part number</td><td>{part.part_number || '—'}</td></tr>
              <tr><td>Category</td><td>{part.category || '—'}</td></tr>
              <tr><td>Manufacturer</td><td>{part.manufacturer || '—'}</td></tr>
              <tr><td>Supplier</td><td>{part.supplier || '—'}</td></tr>
              <tr><td>Unit</td><td>{part.unit || 'each'}</td></tr>
              <tr><td>Location</td><td>{part.location || '—'}</td></tr>
              <tr><td>Cost price</td><td>{part.cost_price > 0 ? `£${part.cost_price.toFixed(2)}` : '—'}</td></tr>
              <tr><td>Sell price</td><td>{part.sell_price > 0 ? `£${part.sell_price.toFixed(2)}` : '—'}</td></tr>
            </tbody>
          </table>
        </div>

        {part.description && (
          <div className="detail-panel">
            <h3>Description</h3>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--ink)' }}>{part.description}</p>
          </div>
        )}

        {specs.length > 0 && (
          <div className="detail-panel">
            <h3>Specifications</h3>
            <table className="spec-table">
              <tbody>
                {specs.map(([key, value]) => (
                  <tr key={key}>
                    <td>{key.replace(/_/g, ' ')}</td>
                    <td>{String(value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {part.datasheet && (
          <div className="form-actions">
            <button className="secondary-button" onClick={openDatasheet}>
              <SafeIcon icon={Icons.Download} size={15} />
              Open Datasheet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
